/** @jsxImportSource @emotion/react */
import ImageLayer from 'ol/layer/Image';
import * as s from './style';
import TileLayer from 'ol/layer/Tile';
import { ImageWMS, TileWMS, XYZ } from 'ol/source';
import React, { useEffect, useRef, useState } from 'react';
import { GEOSERVER } from 'config/URL/URL';
import { Map, View } from 'ol';
import MapFunction from './map_function/MapFunction';
import VectorSource from 'ol/source/Vector';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import GraphPopup from './overlay/graph_popup/GraphPopup';

const CustomMap = ({
    mode,
    featureCollection,
    selectedCategory,
    selectedRadioOption,
    pointVectorLayerVisible,
    mapData,
    setMapData,
    imageLayerVisible,
    imageLayerOpacity,
    selectedSubArray,
    selectedData
}) => {
    const mapRef = useRef();
    const [imageLayerState, setImageLayerState] = useState();

    const [activeLayers, setActiveLayers] = useState();

    useEffect(() => {
        initMap();
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            fetchImageLayer();
        }
    }, [selectedCategory]);

    useEffect(() => {
        let eventKey;

        const option = {
            map: mapRef.current,
            featureCollection: featureCollection,
            activeLayers: activeLayers,
            setActiveLayers: setActiveLayers,
            selectedRadioOption: selectedRadioOption,
            mapData: mapData,
            setMapData: setMapData,
        };

        if (mode) {
            const selectedFunction = MapFunction(mode);
            eventKey = selectedFunction(option).mount();

            return () => {
                selectedFunction(option).dismount(eventKey);
            };
        }
    }, [featureCollection, mode, selectedRadioOption]);

    useEffect(() => {
        if (mapRef.current && activeLayers) {
            activeLayers.forEach((activeLayer) => {
                mapRef.current.addLayer(activeLayer);
            });

            return () => {
                activeLayers.forEach((activeLayer) => {
                    mapRef.current.removeLayer(activeLayer);
                });
            };
        }
    }, [activeLayers]);

    useEffect(() => {
        if (imageLayerState) {
            imageLayerState.setVisible(imageLayerVisible);
        }
    }, [imageLayerVisible]);

    useEffect(() => {
        if (imageLayerState) {
            imageLayerState.setOpacity(imageLayerOpacity);
        }
    }, [imageLayerOpacity]);

    useEffect(() => {
        if (pointVectorLayerVisible) {
            const pointVectorLayer = addPointVectorLayer();

            return () => {
                mapRef.current.removeLayer(pointVectorLayer);
            };
        }
    }, [pointVectorLayerVisible, featureCollection, mode]);

    const initMap = async () => {
        if (!mapRef.current) {
            const vworldLayer = new TileLayer({
                title: 'VWorld white Map',
                visible: true,
                type: 'white',
                source: new XYZ({
                    url: 'https://xdworld.vworld.kr/2d/white/service/{z}/{x}/{y}.png',
                }),
            });

            const sidoWmsLayer = new ImageLayer({
                properties: {
                    layerName: 'sido_wms',
                },
                source: new ImageWMS({
                    url: GEOSERVER + '/geoserver1/wms',
                    params: { LAYERS: 'gis_solution:ts_admin_sido' },
                    ratio: 1,
                    serverType: 'geoserver',
                }),
                opacity: 0.8,
                zIndex: 99,
            });

            const sggWmsLayer = new ImageLayer({
                properties: {
                    layerName: 'sgg_wms',
                },
                source: new ImageWMS({
                    url: GEOSERVER + '/geoserver1/wms',
                    params: { LAYERS: 'gis_solution:ts_admin_sgg' },
                    ratio: 1,
                    serverType: 'geoserver',
                }),
                opacity: 0.8,
                zIndex: 98,
            });

            const view = new View({
                center: [1.420517405965338e7, 4294546.35698363],
                zoom: 7.5,
                maxZoom: 13,
                minZoom: 7,
            });

            const imageLayer = new TileLayer({
                properties: {
                    layerName: 'image_layer',
                },
            });

            setImageLayerState(imageLayer);

            const map = new Map({
                layers: [vworldLayer, sidoWmsLayer, sggWmsLayer, imageLayer],
                target: 'map_area',
                view: view,
            });

            mapRef.current = map;
        }
    };

    const fetchImageLayer = async () => {
        const layerSource = await getDataLayerImageSource(selectedCategory);
        imageLayerState.setSource(layerSource);
    };

    const getDataLayerImageSource = async (category) => {
        const categoryName = category.nm_en;

        const dataLayerSource = new TileWMS({
            url: GEOSERVER + '/geoserver/wms',
            params: {
                LAYERS: 'gis_solution:vl_data',
                viewparams: 'year:2022;keyword:' + categoryName + ';',
                STYLES: 'sty_' + categoryName + '_sgg',
            },
            ratio: 1,
            serverType: 'geoserver',
        });
        return dataLayerSource;
    };

    const addPointVectorLayer = () => {
        const source = new VectorSource({});

        featureCollection.forEach((data) => {
            source.addFeatures(data.featureArray);
        });

        const style = {
            'circle-radius': 2.5,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
            'circle-opacity': 0.9,
            'circle-fill-color': '#0067A3',
        };

        const layer = new WebGLPointsLayer({
            source: source,
            style: style,
            zIndex: 10,
        });

        mapRef.current.addLayer(layer);

        return layer;
    };

    return (
        <div css={s.mapContainer}>
            <div id="map_area" css={s.mapArea}>
                {mode === "pointStatisticsMap" &&
                    <GraphPopup mapData={mapData} selectedSubArray={selectedSubArray} selectedData={selectedData} />
                }
            </div>
        </div>
    );
};

export default CustomMap;
